import { Button, Flex, Form, Space, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { RotateCcw, Save as SaveIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { CategoryEnum } from 'src/api/main'
import { ProTableContainer } from 'src/components'

import type { EditableWorkspaceRole, WorkspacePermissionCatalog } from './types'
import {
  getRoleInitialPermissions,
  groupPermissions,
  isWorkspaceRoleReadOnly,
  type PermissionGroup
} from './utils'
import { WorkspaceRoleColumnHeader } from './WorkspaceRoleColumnHeader'
import { WorkspaceRolePermissionCell } from './WorkspaceRolePermissionCell'

const { Text } = Typography

/** `{ roles: { [roleId]: { [category]: permissionIds } } }` */
type MatrixFormValues = {
  roles: Record<number, Record<string, number[]>>
}

export type RolePermissionUpdate = {
  roleId: number
  permissionIds: number[]
}

type Props = {
  permissions: WorkspacePermissionCatalog[]
  roles: EditableWorkspaceRole[]
  saving?: boolean
  /** False when the viewer lacks `roles.manage` — the matrix turns read-only. */
  canManage?: boolean
  onEditRole: (role: EditableWorkspaceRole) => void
  onDeleteRole: (role: EditableWorkspaceRole) => void
  onSave: (updates: RolePermissionUpdate[]) => void
}

/**
 * Roles × permissions matrix: one row per permission category, one column per
 * workspace role, each cell a multi-select of that category's codes. The same
 * shape as the apps matrix (`RolesPermissionsTable`), with the backend's
 * `category` enum supplying the rows instead of a parsed code prefix.
 *
 * Owner and admin columns render read-only (the backend grants them every
 * permission implicitly), and so do system roles such as "Member". All of them
 * are skipped on save — the backend rejects a PATCH of either.
 */
export const WorkspaceRolesMatrix = ({
  permissions,
  roles,
  saving,
  canManage = true,
  onEditRole,
  onDeleteRole,
  onSave
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<MatrixFormValues>()
  const formValues = Form.useWatch('roles', form) || {}

  const categoryLabel = useCallback(
    (category: CategoryEnum) =>
      t(`workspace.orgRoles.permission.categories.${category}`),
    [t]
  )

  const groups = useMemo(
    () => groupPermissions(permissions, categoryLabel),
    [categoryLabel, permissions]
  )

  const initialValues = useMemo<MatrixFormValues>(
    () => ({
      roles: Object.fromEntries(
        roles.map((role) => [
          role.id,
          getRoleInitialPermissions(role.permissions, groups)
        ])
      )
    }),
    [groups, roles]
  )

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  const setAllForRole = (roleId: number, checked: boolean) => {
    form.setFieldValue(
      ['roles', roleId],
      Object.fromEntries(
        groups.map((group) => [
          group.key,
          checked ? group.permissions.map((p) => p.id) : []
        ])
      )
    )
  }

  const buildRoleColumn = (role: EditableWorkspaceRole) => ({
    title: (
      <WorkspaceRoleColumnHeader
        role={role}
        canManage={canManage}
        onEdit={onEditRole}
        onDelete={onDeleteRole}
        onSelectAll={
          isWorkspaceRoleReadOnly(role)
            ? undefined
            : () => setAllForRole(role.id, true)
        }
        onDeselectAll={
          isWorkspaceRoleReadOnly(role)
            ? undefined
            : () => setAllForRole(role.id, false)
        }
      />
    ),
    key: `role-${role.id}`,
    width: 320,
    render: (_: unknown, record: PermissionGroup) => (
      <Form.Item
        name={['roles', role.id, record.key]}
        style={{ marginBottom: 0 }}
      >
        <WorkspaceRolePermissionCell
          role={role}
          canManage={canManage}
          permissions={record.permissions}
          value={formValues?.[role.id]?.[record.key] ?? []}
          onChange={(value) =>
            form.setFieldValue(['roles', role.id, record.key], [...value])
          }
        />
      </Form.Item>
    )
  })

  const columns: ColumnsType<PermissionGroup> = [
    {
      title: t('workspace.orgRoles.matrix.moduleColumn'),
      dataIndex: 'label',
      key: 'label',
      width: 220,
      fixed: 'left' as const,
      render: (label: string) => <Text strong>{label}</Text>
    },
    ...roles.map(buildRoleColumn)
  ]

  const handleSave = () => {
    const values = form.getFieldsValue() as MatrixFormValues
    const updates = roles
      .filter((role) => !isWorkspaceRoleReadOnly(role))
      .map((role) => ({
        roleId: role.id,
        permissionIds: Object.values(values.roles?.[role.id] ?? {}).flat()
      }))

    onSave(updates)
  }

  return (
    <Form form={form} initialValues={initialValues}>
      <ProTableContainer<PermissionGroup>
        options={false}
        columnsReorderable={false}
        showSearch={false}
        rowKey="key"
        bordered
        dataSource={groups}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        footer={() => (
          <Flex justify="space-between" align="center" gap={8} wrap="wrap">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgRoles.matrix.hint')}
            </Text>
            {canManage ? (
              <Space>
                <Button
                  icon={<RotateCcw size={14} />}
                  onClick={() => form.setFieldsValue(initialValues)}
                >
                  {t('workspace.orgRoles.matrix.reset')}
                </Button>
                <Button
                  type="primary"
                  icon={<SaveIcon size={14} />}
                  loading={saving}
                  onClick={handleSave}
                >
                  {t('workspace.orgRoles.save')}
                </Button>
              </Space>
            ) : null}
          </Flex>
        )}
      />
    </Form>
  )
}
